import { endOfDay } from 'date-fns';

import {
  Attribute,
  CategoryChildrenName,
  Product,
  ProductInput,
  ProductOverWorkConditions,
  SupportedProductPublicationStatus,
} from '@app/types/catalog';
import { notRequireClinicalDepartmentName } from '@app/utils/constants';
import { formatDate, formatTime, formatWeek } from '@app/utils/format';
import { isAgencyOrg } from '@app/utils/organization';

/**
 * "YYYY年MM月DD日(曜)"形式または"月曜・火曜・水曜"形式の求人タイトルを取得する。
 * @param product 求人
 * @returns タイトル
 */
export function getProductDayWeekTitle(product: Product | undefined) {
  if (!product) return '';

  const { category, customFields } = product;
  const { name } = category;

  if (
    name === CategoryChildrenName.PRO_DOCTOR_SPOT ||
    name === CategoryChildrenName.PARAMEDICAL_SPOT
  ) {
    return formatDate(customFields?.day);
  }

  if (
    name === CategoryChildrenName.PRO_DOCTOR_REGULAR_PART_TIME ||
    name === CategoryChildrenName.PARAMEDICAL_REGULAR_PART_TIME
  ) {
    return formatWeek(customFields?.repeatWeek);
  }

  return '';
}

/**
 * "YYYY年MM月DD日(曜) hh:mm~hh:mm"形式または"月曜・火曜・水曜 hh:mm~hh:mm"形式の求人タイトルを取得する。
 * @param product 求人
 * @returns タイトル
 */
export function getProductDayWeekTimeTitle(product: Product | undefined) {
  return `${getProductDayWeekTitle(product)} ${formatTime(
    product?.customFields?.startTime
  )}~${formatTime(product?.customFields?.endTime)}`;
}

/** 求人一覧・詳細のベースパス。ルート定義とnavigate先の唯一の定義元 */
export const ProductBasePath = {
  FULL_TIME: 'products/fulltime',
  PARAMEDICAL: 'paramedical-products',
  SPOT_REGULAR: 'products/spot-regular',
} as const;

export type ProductListBasePath =
  (typeof ProductBasePath)[keyof typeof ProductBasePath];

/**
 * 求人の案件区分(category.name)が常勤かどうかを判定する。
 * NOTE:'常勤'は'定期非常勤'の部分文字列のため、必ず===で比較すること
 * @param categoryName 求人のcategory.name
 * @returns 常勤ならtrue
 */
export function isFullTimeProduct(categoryName: string | undefined): boolean {
  return categoryName === CategoryChildrenName.PRO_DOCTOR_FULL_TIME;
}

/**
 * 求人が人材紹介企業の求人かどうかを判定する。
 * NOTE:評価の表示可否は閲覧中の組織ではなく求人の掲載組織で判定する
 * @param product 求人
 * @returns 人材紹介企業の求人ならtrue
 */
export function isAgencyProduct(
  product?: Pick<Product, 'organization'>
): boolean {
  return isAgencyOrg(product?.organization);
}

/**
 * 現在のURLパスから求人一覧の種別(常勤/スポット・定期非常勤/パラメディカル)を判定し、
 * 対応するベースパスを返す。ProductLayout・AdminOrganizationDelegationLayout・
 * ProductManagement・ProductEditStatus・ProductBulkEditで共通利用する。
 * NOTE:パスセグメント単位の完全一致で判定する(includes()による部分一致誤判定を避けるため)
 * @param pathname 現在のURLパス(location.pathname)
 * @returns ベースパス
 */
export function getProductListBasePath(pathname: string): ProductListBasePath {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.includes(ProductBasePath.PARAMEDICAL)) {
    return ProductBasePath.PARAMEDICAL;
  }
  const productsIndex = segments.indexOf('products');
  if (productsIndex !== -1 && segments[productsIndex + 1] === 'fulltime') {
    return ProductBasePath.FULL_TIME;
  }
  return ProductBasePath.SPOT_REGULAR;
}

/**
 * 求人の案件区分(category.name)から対応する求人一覧のベースパスを判定する。
 * getProductListBasePath(pathname → 種別)の逆方向(category → path)。
 * ReviewForm・OrderDetails・OrderInfo・ProductListなど、求人詳細への
 * リンク先を組み立てる箇所で共通利用する。
 * @param categoryName 求人のcategory.name
 * @returns ベースパス
 */
export function getProductBasePathByCategory(
  categoryName: string | undefined
): ProductListBasePath {
  if (
    categoryName === CategoryChildrenName.PARAMEDICAL_SPOT ||
    categoryName === CategoryChildrenName.PARAMEDICAL_REGULAR_PART_TIME
  ) {
    return ProductBasePath.PARAMEDICAL;
  }
  if (isFullTimeProduct(categoryName)) {
    return ProductBasePath.FULL_TIME;
  }
  return ProductBasePath.SPOT_REGULAR;
}

/** 年収の入力・表示は万円単位、保存は円単位。その換算単位 */
const ANNUAL_SALARY_MAN_YEN = 10000;

/** 常勤求人の年収。下限はvariants[].price.amount、上限等はcustomFieldsに格納 */
export interface AnnualSalary {
  /** 給与を応相談表示にするかどうか */
  annualSalaryNegotiable: boolean;
  /** 年収固定(下限のみが検索対象)かどうか */
  fixed: boolean;
  /** 年収下限(円単位) */
  from: number;
  /** 年収上限無し("下限年収〜"表示にする)かどうか */
  noUpperLimit: boolean;
  /** 年収上限(円単位)。fixed/noUpperLimitがtrueの場合は無視される */
  to: number;
}

/**
 * 常勤求人の年収を取得する。
 * @param product 求人(常勤カテゴリを想定)
 * @returns 年収(下限・上限・固定/上限無しフラグ)
 */
export function getAnnualSalary(product: Product): AnnualSalary {
  const lastVariant = product.variants?.[product.variants.length - 1];
  return {
    annualSalaryNegotiable: !!product.customFields?.annualSalaryNegotiable,
    fixed: !!product.customFields?.annualSalaryFixed,
    from: lastVariant?.price.amount ?? 0,
    noUpperLimit: !!product.customFields?.annualSalaryNoUpperLimit,
    to: product.customFields?.annualSalaryTo ?? 0,
  };
}

/**
 * フォーム入力値(万円単位)を保存用の年収(円単位)に変換する。
 * @param manYen 年収(万円単位)
 * @returns 年収(円単位)
 */
export function toAnnualSalaryYen(manYen: number | null): number {
  return (manYen ?? 0) * ANNUAL_SALARY_MAN_YEN;
}

/**
 * 保存されている年収(円単位)をフォーム入力値(万円単位)に変換する。
 * @param yen 年収(円単位)
 * @returns 年収(万円単位)。未設定の場合はnull
 */
export function toAnnualSalaryManYen(yen: number | undefined): number | null {
  return yen ? Math.round(yen / ANNUAL_SALARY_MAN_YEN) : null;
}

/**
 * 年収を表示用文字列(万円単位)に整形する。
 * 応相談なら"応相談"、年収固定なら"2,500万円"、上限無しなら"2,500万円〜"、
 * それ以外は"2,500万円〜3,000万円"のような範囲表示になる。
 * @param annualSalary 年収
 * @returns 表示用文字列
 */
export function formatAnnualSalary(annualSalary: AnnualSalary): string {
  if (annualSalary.annualSalaryNegotiable) {
    return '応相談';
  }
  const from = Math.round(
    annualSalary.from / ANNUAL_SALARY_MAN_YEN
  ).toLocaleString();
  if (annualSalary.fixed) {
    return `${from}万円`;
  }
  if (annualSalary.noUpperLimit) {
    return `${from}万円〜`;
  }
  const to = Math.round(
    annualSalary.to / ANNUAL_SALARY_MAN_YEN
  ).toLocaleString();
  return `${from}万円〜${to}万円`;
}

/**
 * 掲載期間を求人のpublicationに変換する。
 * NOTE:終了日の指定方法によっては開始日より前の日になる可能性があるため、その場合は開始日に揃える
 */
export function toProductPublication(
  startPeriod: Date,
  endPeriod: Date | undefined,
  status: SupportedProductPublicationStatus
): ProductInput['publication'] {
  return {
    since: startPeriod.toISOString(),
    status,
    until:
      startPeriod.getTime() <= (endPeriod?.getTime() ?? 0)
        ? endPeriod
          ? endOfDay(endPeriod).toISOString()
          : ''
        : endOfDay(startPeriod).toISOString(),
  };
}

/**
 * 一括編集で勤務地情報が求人間で混在しているか判定するためのキー。
 * NOTE:勤務地情報として扱う項目を1箇所で定義する
 */
export function workLocationMixKey(product: Product): string {
  const { access, workAddress1, workAddress2, workPostalCode } =
    product.customFields ?? {};
  const locations = product.locations?.map((l) => l.name).join(' ');
  return `${workPostalCode ?? ''} ${locations} ${workAddress1 ?? ''} ${
    workAddress2 ?? ''
  } ${access ?? ''}`;
}

/** 残業・時間調整の入力値を求人のoverWorkConditionsに変換する */
export function toOverWorkConditions(formValue: {
  overtimeAllowance: boolean;
  possibleOvertime: boolean;
  possibleTimeAdjust: boolean;
}): ProductOverWorkConditions[] {
  return [
    ...(formValue.possibleTimeAdjust ? (['時間調整可'] as const) : []),
    ...(formValue.possibleOvertime ? (['残業あり'] as const) : []),
    ...(formValue.possibleOvertime && formValue.overtimeAllowance
      ? (['残業代の追加あり'] as const)
      : []),
  ];
}

/** 求人のoverWorkConditionsをフォームの入力値に戻す */
export function fromOverWorkConditions(
  overWorkConditions: ProductOverWorkConditions[] | undefined
): {
  overtimeAllowance: boolean;
  possibleOvertime: boolean;
  possibleTimeAdjust: boolean;
} {
  const has = (value: ProductOverWorkConditions) =>
    !!overWorkConditions?.some((v) => v === value);
  return {
    overtimeAllowance: has('残業代の追加あり'),
    possibleOvertime: has('残業あり'),
    possibleTimeAdjust: has('時間調整可'),
  };
}

export function getCalculatedHourlyPay(
  startTime = '',
  endTime = '',
  breakTime: number | null | undefined,
  variantsPriceAmount: number | null
) {
  /** 勤務開始時間 */
  const startMinutes = startTime
    .split(':')
    .map((v) => Number(v))
    .reduce((r, v, i) => (i === 0 ? v * 60 : r + v), 0);
  /** 勤務終了時間 */
  const endMinutes = endTime
    .split(':')
    .map((v) => Number(v))
    .reduce((r, v, i) => (i === 0 ? v * 60 : r + v), 0);
  /** 勤務時間（終了時間-開始時間-休憩時間） */
  const workHour = (endMinutes - startMinutes - (breakTime ?? 0)) / 60;
  /** 想定時給 */
  const calculatedHourlyPay = variantsPriceAmount
    ? Math.trunc(variantsPriceAmount / workHour)
    : 0; // 日給の場合は報酬金額÷勤務時間
  return calculatedHourlyPay;
}

export function getCalculatedDairyPay(
  startTime = '',
  endTime = '',
  breakTime: number | null | undefined,
  variantsPriceAmount: number | null
) {
  /** 勤務開始時間 */
  const startMinutes = startTime
    .split(':')
    .map((v) => Number(v))
    .reduce((r, v, i) => (i === 0 ? v * 60 : r + v), 0);
  /** 勤務終了時間 */
  const endMinutes = endTime
    .split(':')
    .map((v) => Number(v))
    .reduce((r, v, i) => (i === 0 ? v * 60 : r + v), 0);
  /** 勤務時間（終了時間-開始時間-休憩時間） */
  const workHour = (endMinutes - startMinutes - (breakTime ?? 0)) / 60;
  /** 想定日給 */
  const calculatedDairyPay = variantsPriceAmount
    ? Math.trunc(variantsPriceAmount * workHour)
    : 0; // 時給の場合は報酬金額×勤務時間
  return calculatedDairyPay;
}

export const formatAttributes = (attributes: Attribute[]): Attribute[] => {
  return (
    attributes
      .filter(
        (clinical) =>
          clinical.items[0]?.value !== notRequireClinicalDepartmentName
      )
      ?.reduce((items: Attribute[], item: Attribute) => {
        const foundGroup = items.find(
          (i) => i.items[0]?.groupName === item.items[0]?.groupName
        );
        if (foundGroup) {
          foundGroup.children?.push(item);
        } else {
          items.push({
            ...item,
            children: [item],
          });
        }
        return items;
      }, []) || []
  );
};
